import React, { PropsWithChildren, useContext } from 'react'

import { useTranslation } from 'react-i18next'

import { IsInArcadeModalContext } from '@cash-design-system/react'
import { HelpItem } from '@cash-web/protos/squareup/franklin/api/help_item.pb'
import { ModalPortal } from '@cash-web/shared-feature-modal'
import { FlowContextProps } from '@cash-web/shared-flow-data-access-context'

import { HelpModalComponent } from './HelpModalComponent'

interface CustomActionResponse {
  success: boolean
  message?: string
}

export interface HelpModalItem extends HelpItem {
  // This is meant to support legacy blockers with hard-coded help action items that are not defined
  // in the proto
  customAction?: () => Promise<CustomActionResponse>
}

interface HelpModalProps {
  flow: FlowContextProps
  blockerActionOptions?: {
    replace?: boolean
  }
  showInModal?: boolean
  onClose: () => void
  isOpen: boolean
  helpItems: HelpModalItem[]
  title?: string
  subtext?: string
}

export const HelpModal = ({
  showInModal,
  isOpen,
  helpItems,
  title,
  subtext,
  onClose,
  flow,
  blockerActionOptions,
  children,
}: PropsWithChildren<HelpModalProps>) => {
  const isInArcadeModal = useContext(IsInArcadeModalContext)
  const shouldShowInModal = !isInArcadeModal
  const showModal = showInModal ?? shouldShowInModal

  const { t } = useTranslation()

  const backButton = {
    text: t('Back', { context: 'Button to return the user to the previous state' }),
    customAction: async () => {
      return {
        success: true,
        message: '',
      }
    },
  } satisfies HelpModalItem

  if (helpItems.length === 0 || !isOpen) {
    return children
  }

  if (!showModal) {
    return (
      <HelpModalComponent
        onClose={onClose}
        flow={flow}
        blockerActionOptions={blockerActionOptions}
        helpItems={[...helpItems, backButton]}
        title={title}
        subtext={subtext}
      />
    )
  }

  return (
    <>
      {children}
      <ModalPortal closeButtonProps={{ onClose: onClose }}>
        <HelpModalComponent
          onClose={onClose}
          flow={flow}
          blockerActionOptions={blockerActionOptions}
          helpItems={helpItems}
          title={title}
          subtext={subtext}
        />
      </ModalPortal>
    </>
  )
}
